import * as React from "react";
import { FamilyMember, Page } from "../../types";
import InputMask from "react-input-mask";
import update from "react-addons-update";
import FamilyMemberEntry from "./FamilyMemberEntry";
import PhoneNumberDisplay from "../../../components/PhoneNumberDisplay";

interface OwnProps {
  familyMembers: Page<FamilyMember> | null;
  addFamilyMember: (familyMember: FamilyMember) => void;
  deleteFamilyMember: (familyMember: FamilyMember) => void;
}

interface OwnState {
  submitDisabled: boolean;
  addingMembers: boolean;
  newFamilyMembers: FamilyMember[];
  formError: string | null;
  editingFamilyMember: string | undefined;
}

type Props = OwnProps;

class FamilyMembers extends React.PureComponent<Props, OwnState> {
  public constructor(props: Props) {
    super(props);

    const existingFamilyMembers = [];
    for (let i = 0; i < 5; i++) {
      existingFamilyMembers.push({ name: "", relation: "", phone: "" });
    }

    this.state = {
      submitDisabled: true,
      addingMembers: false,
      newFamilyMembers: this.emptyArray(5),
      formError: null,
      editingFamilyMember: undefined
    };
  }

  private createEmptyMembers = () => {
    this.setState({ newFamilyMembers: this.emptyArray(5) });
  };

  private emptyArray = (count: number): FamilyMember[] => {
    const arr: FamilyMember[] = [];
    for (let i = 0; i < count; i++) {
      arr.push({ name: undefined, relation: undefined, phone: undefined });
    }
    return arr;
  };

  private submitFamilyMembers = (list: FamilyMember[]) => {
    list.forEach(m => this.props.addFamilyMember(m));
  };

  private checkFamilyMembers = () => {
    const listToAdd: FamilyMember[] = [];
    let errorMessage = null;
    for (const m of this.state.newFamilyMembers) {
      if (m.name || m.relation || m.phone) {
        if (!m.phone) {
          errorMessage = "All added family member must have phone numbers.";
          break;
        }

        if (m.phone?.replace(/[^0-9]/g, "").length !== 10) {
          errorMessage = "Please check phone numbers.";
          break;
        } else {
          m.phone = m.phone?.replace(/[^0-9]/g, "");
          listToAdd.push(m);
        }
      }
    }
    if (errorMessage) {
      this.setState({ formError: errorMessage });
    } else if (listToAdd.length > 0) {
      this.submitFamilyMembers(listToAdd);
      this.setState({ addingMembers: false, newFamilyMembers: [] }, () =>
        this.createEmptyMembers()
      );
    } else {
      this.setState({ addingMembers: false, newFamilyMembers: [] }, () =>
        this.createEmptyMembers()
      );
    }
  };

  private familyRelationInputChanged = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    this.setState({
      newFamilyMembers: update(this.state.newFamilyMembers, {
        [index]: { relation: { $set: e.target.value } }
      })
    });
  };

  private familyNameInputChanged = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    this.setState({
      newFamilyMembers: update(this.state.newFamilyMembers, {
        [index]: { name: { $set: e.target.value } }
      })
    });
  };

  private familyPhoneInputChanged = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    this.setState({
      newFamilyMembers: update(this.state.newFamilyMembers, {
        [index]: { phone: { $set: e.target.value } }
      })
    });
  };

  private addEntryToNewFamilyMembers = () => {
    this.setState({
      newFamilyMembers: update(this.state.newFamilyMembers, { $push: this.emptyArray(1) })
    });
  };

  private showAddFamilyMembers = () => {
    this.setState({ addingMembers: true });
  };

  private deleteMember = (member: FamilyMember) => {
    this.props.deleteFamilyMember(member);
  };

  public render() {
    const { familyMembers, deleteFamilyMember } = this.props;
    const { editingFamilyMember } = this.state;
    return (
      <>
        <div className="section p-15 mt-25">
          <div className="title">Family Members</div>
          {familyMembers && familyMembers.results.length > 0 && (
            <div className="mt-20 mb-30">
              <div>
                <div className="patient header">
                  <div className="flex-grid full-width">
                    <div className="flex-1">Relation</div>
                    <div className="flex-1">Name</div>
                    <div className="flex-1 needs-required">Phone</div>
                    <div className="action double" />
                  </div>
                </div>
                {familyMembers.results.map((familyMember, index) => (
                  <div key={index} className="patient">
                    {editingFamilyMember != familyMember.id && (
                      <div className="flex-grid align-center full-width ">
                        <div className="flex-1">{familyMember.relation}</div>
                        <div className="flex-1">{familyMember.name}</div>
                        <div className="flex-1">
                          <PhoneNumberDisplay phonenumber={familyMember.phone} />
                        </div>
                        <div className="action double flex">
                          <button
                            className="link mr-25"
                            onClick={() => this.setState({ editingFamilyMember: familyMember.id })}
                          >
                            <i className="far fa-edit"></i>Edit
                          </button>
                          <button className="link" onClick={() => deleteFamilyMember(familyMember)}>
                            <i className="far fa-trash-alt" />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                    {editingFamilyMember === familyMember.id && (
                      <FamilyMemberEntry
                        familyMember={familyMember}
                        clearEditing={() => this.setState({ editingFamilyMember: undefined })}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {familyMembers && familyMembers.results.length === 0 && !this.state.addingMembers && (
            <div className="mt-20">There are currently no family members enrolled.</div>
          )}
          {!this.state.addingMembers && (
            <div className="flex-end">
              <button className="mt-20" onClick={this.showAddFamilyMembers}>
                Add Family Member(s)
              </button>
            </div>
          )}
          {this.state.addingMembers && (
            <div className="mt-20">
              Add only as many family members you need. A <span className="bold">phone number</span>{" "}
              is all that is required to delivery a message.
              <div className="mt-20">
                <div className="flex">
                  <div className="flex-1">Relationship</div>
                  <div className="flex-1 pl-5 pr-5">Name</div>
                  <div className="flex-1">
                    <span className="needs-required">Phone Number</span>
                  </div>
                </div>
              </div>
              {this.state.newFamilyMembers.map((familyMember, index) => (
                <div key={index} className="mt-10">
                  <div className="flex">
                    <div className="flex-1">
                      <input
                        className="form-control"
                        defaultValue={familyMember.relation}
                        onChange={event => this.familyRelationInputChanged(event, index)}
                        placeholder="Relation"
                      />
                    </div>
                    <div className="flex-1 pl-5 pr-5">
                      <input
                        className="form-control"
                        defaultValue={familyMember.name}
                        onChange={event => this.familyNameInputChanged(event, index)}
                        placeholder="Name"
                      />
                    </div>
                    <div className="flex-1">
                      <InputMask
                        className="form-control text-center"
                        onChange={event => this.familyPhoneInputChanged(event, index)}
                        defaultValue={familyMember.phone}
                        mask="(999) 999-9999"
                        type="tel"
                        minLength={10}
                        pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <div className="form-error red center mt-10">{this.state.formError}</div>
              <div className="flex-between">
                <button className="mt-20" onClick={this.addEntryToNewFamilyMembers}>
                  Add another entry
                </button>
                <button className="mt-20 highlight" onClick={this.checkFamilyMembers}>
                  Add Family Member(s)
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
}

export default FamilyMembers;
